import { Capacitor } from '@capacitor/core';
import { motion } from 'motion/react';
import { Download } from 'lucide-react';
import { Button } from './ui/button';
import { AuthBackground } from './auth/AuthBackground';
import { resolveStoreTarget } from '../../lib/appStore';

interface ForceUpdateScreenProps {
  message: string;
  currentVersion: string;
  minVersion: string;
  storeUrl: string;
}

export function ForceUpdateScreen({ message, currentVersion, minVersion, storeUrl }: ForceUpdateScreenProps) {
  const store = resolveStoreTarget(Capacitor.getPlatform(), storeUrl);

  const handleUpdate = () => {
    if (!store) return;
    window.open(store.url, store.target);
  };

  return (
    <div className="min-h-screen relative flex flex-col overflow-hidden">
      <AuthBackground />

      {/* Content */}
      <div
        className="relative z-10 flex-1 flex flex-col items-center justify-center px-6"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        {/* Logo with animated battery ring */}
        <motion.div
          className="relative mb-8"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, type: 'spring' }}
        >
          {/* Pulsing glow */}
          <motion.div
            className="absolute inset-0 rounded-full bg-[#8bc43e]"
            animate={{
              scale: [1, 1.6, 1],
              opacity: [0.25, 0, 0.25],
            }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeOut' }}
          />
          {/* Rotating ring — "charging" indicator */}
          <motion.div
            className="absolute -inset-3 rounded-full border-2 border-transparent"
            style={{
              borderTopColor: '#8bc43e',
              borderRightColor: '#8bc43e40',
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          />
          {/* Logo */}
          <img
            src="./eveebuddy-logo.png"
            alt="EVee Buddy"
            className="w-24 h-24 relative z-10 drop-shadow-lg"
          />
        </motion.div>

        {/* Brand name */}
        <motion.h2
          className="text-2xl font-bold text-foreground mb-1"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          EVee Buddy
        </motion.h2>
        <motion.p
          className="text-xs text-muted-foreground mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          for a worry free travel!
        </motion.p>

        {/* Update card */}
        <motion.div
          className="w-full max-w-sm bg-background rounded-2xl shadow-xl border border-border/30 p-6"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, type: 'spring', damping: 25, stiffness: 200 }}
        >
          {/* Animated charging bolt */}
          <div className="flex justify-center mb-4">
            <motion.div
              className="w-14 h-14 rounded-2xl bg-[#8bc43e]/10 flex items-center justify-center"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <motion.path
                  d="M13 2L4 14h7l-2 8 9-12h-7l2-8z"
                  fill="#8bc43e"
                  initial={{ opacity: 0.5 }}
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                />
              </svg>
            </motion.div>
          </div>

          <h1 className="text-lg font-semibold text-foreground text-center mb-1">
            New Version Available
          </h1>
          <p className="text-sm text-muted-foreground text-center mb-5 leading-relaxed">
            {message}
          </p>

          {/* Version comparison — styled as a progress indicator */}
          <div className="relative bg-muted/50 rounded-xl p-4 mb-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-center flex-1">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Installed</p>
                <span className="text-sm font-mono font-semibold text-muted-foreground">v{currentVersion}</span>
              </div>
              {/* Animated arrow */}
              <motion.div
                className="mx-3 flex-shrink-0"
                animate={{ x: [0, 4, 0] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
              >
                <svg width="20" height="12" viewBox="0 0 20 12" fill="none">
                  <path d="M0 6h16M12 1l5 5-5 5" stroke="#8bc43e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.div>
              <div className="text-center flex-1">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Required</p>
                <span className="text-sm font-mono font-semibold text-[#8bc43e]">v{minVersion}</span>
              </div>
            </div>
            {/* Progress bar */}
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-[#8bc43e]/60 to-[#8bc43e]"
                initial={{ width: '0%' }}
                animate={{ width: '60%' }}
                transition={{ delay: 0.6, duration: 1, ease: 'easeOut' }}
              />
            </div>
          </div>

          {/* Update button — matches login screen style */}
          {store ? (
            <Button
              onClick={handleUpdate}
              className="w-full h-12 bg-gradient-to-r from-[#8bc43e] to-[#6fa02e] hover:from-[#7ab32e] hover:to-[#5f8a26] text-white rounded-xl font-semibold shadow-lg shadow-[#8bc43e]/25 border-0"
            >
              <Download className="w-4 h-4 mr-2" />
              Update Now
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground text-center">
              An update is required. Please install the latest version from your app store.
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
}
